import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Pill, brl, num, dt } from "./_shared";
import { Plus, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function AdminCoupons() {
  const [tab, setTab] = useState("list");
  const [rows, setRows] = useState([]);
  const [report, setReport] = useState(null);
  const [creating, setCreating] = useState(false);
  const [d, setD] = useState({ code: "", type: "percent", percent_off: 10, amount_off: "", max_uses: 0, valid_until: "" });

  const load = () => api.get("/admin/coupons").then(r => setRows(r.data.coupons));
  const loadReport = () => api.get("/admin/coupons/report").then(r => setReport(r.data));
  useEffect(() => { load(); loadReport(); }, []);

  const create = async () => {
    if (!d.code.trim()) return alert("Código obrigatório");
    const body = {
      code: d.code.trim(),
      max_uses: parseInt(d.max_uses) || 0,
      valid_until: d.valid_until || "",
    };
    if (d.type === "percent") {
      body.percent_off = parseInt(d.percent_off) || 0;
    } else {
      const cents = Math.round(parseFloat(String(d.amount_off).replace(",", ".") || "0") * 100);
      if (cents < 100) return alert("Valor mínimo do desconto: R$ 1,00");
      body.amount_off_cents = cents;
    }
    try {
      const r = await api.post("/admin/coupons", body);
      if (r.data.stripe_sync_error) {
        alert(`Cupom criado, mas falhou sincronizar no Stripe: ${r.data.stripe_sync_error}`);
      }
      setD({ code: "", type: "percent", percent_off: 10, amount_off: "", max_uses: 0, valid_until: "" });
      setCreating(false);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha ao criar cupom");
    }
  };

  const toggle = async (c) => {
    await api.patch(`/admin/coupons/${c.id}`, { status: c.status === "active" ? "inactive" : "active" });
    load();
  };

  const resync = async (id) => {
    try {
      const r = await api.post(`/admin/coupons/${id}/resync`);
      if (r.data.ok) alert("Cupom sincronizado no Stripe");
      else alert(r.data.coupon?.stripe_sync_error || "Falha ao sincronizar");
      load();
    } catch (e) { alert("Falha ao sincronizar"); }
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-coupons">
      <PageHeader title="Cupons e descontos" subtitle="Central privada do OWNER"
        right={
          <div className="flex items-center gap-2">
            <div className="flex bg-[#0f1524] border border-white/5 rounded-xl p-1">
              <button onClick={()=>setTab("list")} data-testid="coupons-tab-list"
                className={`px-3 py-1.5 rounded-lg text-xs ${tab==="list" ? "bg-[#d4a24c] text-black font-semibold" : "text-slate-300"}`}>
                Cupons
              </button>
              <button onClick={()=>{setTab("report"); loadReport();}} data-testid="coupons-tab-report"
                className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1 ${tab==="report" ? "bg-[#d4a24c] text-black font-semibold" : "text-slate-300"}`}>
                <BarChart3 className="w-3.5 h-3.5"/> Relatório
              </button>
            </div>
            {tab === "list" && (
              <button onClick={()=>setCreating(true)}
                className="px-4 py-2 rounded-lg bg-[#d4a24c] text-black text-sm font-semibold flex items-center gap-2"
                data-testid="admin-coupons-new"><Plus className="w-4 h-4"/>Novo cupom</button>
            )}
          </div>
        }/>

      {tab === "list" && creating && (
        <Card className="mb-6" title="Novo cupom" testid="admin-coupons-form">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input placeholder="Código *" value={d.code} onChange={e=>setD({...d, code: e.target.value.toUpperCase()})}
              className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-coupon-code"/>
            <select value={d.type} onChange={e=>setD({...d, type: e.target.value})}
              className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-coupon-type">
              <option value="percent">Percentual</option>
              <option value="amount">Valor fixo (R$)</option>
            </select>
            {d.type === "percent" ? (
              <input placeholder="% off" type="number" min="1" max="100" value={d.percent_off} onChange={e=>setD({...d, percent_off: e.target.value})}
                className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-coupon-percent"/>
            ) : (
              <input placeholder="R$ 10,00" value={d.amount_off} onChange={e=>setD({...d, amount_off: e.target.value})}
                className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-coupon-amount"/>
            )}
            <input placeholder="Máx. usos (0 ilim.)" type="number" value={d.max_uses} onChange={e=>setD({...d, max_uses: e.target.value})}
              className="bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-coupon-max"/>
            <input placeholder="Válido até (opcional)" type="date" value={d.valid_until} onChange={e=>setD({...d, valid_until: e.target.value})}
              className="md:col-span-2 bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="admin-coupon-until"/>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={create} className="px-4 py-2 rounded-lg bg-[#d4a24c] text-black text-sm font-semibold" data-testid="admin-coupon-save">Criar</button>
            <button onClick={()=>setCreating(false)} className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-sm">Cancelar</button>
          </div>
        </Card>
      )}

      {tab === "list" && (<>
      <div className="mb-4 p-3 rounded-lg bg-emerald-900/20 border border-emerald-800/50 text-xs text-emerald-200 flex items-start gap-2" data-testid="admin-coupons-info">
        <span className="text-emerald-400">●</span>
        <div>
          <b>Cupons são espelhados automaticamente no Stripe.</b> Ao criar um código aqui, ele fica imediatamente ativo em todos os checkouts (cobranças, planos e honorários) — o cliente digita o código na tela de pagamento e o desconto é aplicado.
        </div>
      </div>

      <Card testid="admin-coupons-list">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="py-3 pr-4">Código</th><th className="py-3 pr-4">Desconto</th>
              <th className="py-3 pr-4">Usos / Máx.</th><th className="py-3 pr-4">Validade</th>
              <th className="py-3 pr-4">Status</th><th className="py-3 pr-4">Stripe</th>
              <th className="py-3 pr-4">Criado</th>
              <th className="py-3 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="8" className="py-8 text-center text-slate-500">Nenhum cupom criado</td></tr>}
            {rows.map(c => {
              const discount = c.percent_off > 0
                ? `${c.percent_off}%`
                : c.amount_off_cents > 0
                  ? `R$ ${(c.amount_off_cents/100).toFixed(2)}`
                  : "—";
              const synced = !!c.stripe_promo_id;
              return (
                <tr key={c.id} className="border-b border-white/5" data-testid={`admin-coupon-${c.code}`}>
                  <td className="py-3 pr-4 font-mono text-slate-100">{c.code}</td>
                  <td className="py-3 pr-4 text-slate-300">{discount}</td>
                  <td className="py-3 pr-4 text-slate-300">{c.uses}/{c.max_uses || "∞"}</td>
                  <td className="py-3 pr-4 text-slate-400">{c.valid_until ? new Date(c.valid_until).toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="py-3 pr-4"><Pill tone={c.status === "active" ? "green" : "slate"}>{c.status}</Pill></td>
                  <td className="py-3 pr-4">
                    {synced ? (
                      <Pill tone="gold" testid={`coupon-stripe-${c.code}`}>Ativo no Stripe</Pill>
                    ) : c.stripe_sync_error ? (
                      <span className="inline-flex items-center gap-1 text-[11px]">
                        <Pill tone="red">Falha sync</Pill>
                        <button onClick={()=>resync(c.id)} className="text-slate-400 hover:text-[#d4a24c]" title={c.stripe_sync_error} data-testid={`coupon-resync-${c.code}`}>↻</button>
                      </span>
                    ) : (
                      <button onClick={()=>resync(c.id)} className="text-xs text-slate-400 hover:text-[#d4a24c]" data-testid={`coupon-sync-${c.code}`}>Sincronizar</button>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-slate-400 text-xs">{dt(c.created_at)}</td>
                  <td className="py-3 pr-4 text-right">
                    <button onClick={()=>toggle(c)}
                      className="px-3 py-1 rounded-lg border border-white/10 text-xs text-slate-200 hover:bg-white/5"
                      data-testid={`admin-coupon-toggle-${c.code}`}>
                      {c.status === "active" ? "Pausar" : "Ativar"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      </>)}

      {tab === "report" && report && (
        <div data-testid="admin-coupons-report">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <div className="rounded-2xl border border-white/5 bg-[#111726]/70 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Cupons ativos</div>
              <div className="text-2xl font-serif text-[#d4a24c] mt-1">{num(report.totals.coupons_active)}<span className="text-sm text-slate-500">/{num(report.totals.coupons_total)}</span></div>
            </div>
            <div className="rounded-2xl border border-white/5 bg-[#111726]/70 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Resgates</div>
              <div className="text-2xl font-serif text-slate-100 mt-1">{num(report.totals.redemptions)}</div>
            </div>
            <div className="rounded-2xl border border-white/5 bg-[#111726]/70 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Receita gerada</div>
              <div className="text-2xl font-serif text-emerald-400 mt-1">{brl(report.totals.revenue_cents)}</div>
            </div>
            <div className="rounded-2xl border border-white/5 bg-[#111726]/70 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Descontos concedidos</div>
              <div className="text-2xl font-serif text-rose-400 mt-1">{brl(report.totals.discount_cents)}</div>
            </div>
            <div className="rounded-2xl border border-white/5 bg-[#111726]/70 p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Ticket médio</div>
              <div className="text-2xl font-serif text-slate-100 mt-1">{brl(report.totals.avg_ticket_cents)}</div>
            </div>
          </div>

          {report.rows.some(r => r.revenue_cents > 0) && (
            <Card className="mb-6" title="Top cupons por receita" kicker="Ranking">
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={report.rows.filter(r => r.revenue_cents > 0).slice(0,10).map(r => ({ code: r.code, valor: r.revenue_cents/100 }))}>
                    <CartesianGrid stroke="#1e293b" strokeDasharray="3 3"/>
                    <XAxis dataKey="code" stroke="#64748b" fontSize={10} angle={-30} textAnchor="end" height={60}/>
                    <YAxis stroke="#64748b" fontSize={11}/>
                    <Tooltip contentStyle={{background:"#0f1524", border:"1px solid #1e293b", color:"#e2e8f0"}}
                      formatter={(v)=>new Intl.NumberFormat("pt-BR", {style:"currency", currency:"BRL"}).format(v)}/>
                    <Bar dataKey="valor" fill="#d4a24c" radius={[6,6,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          <Card title="Desempenho por cupom" testid="coupons-report-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <th className="py-3 pr-4">Código</th>
                    <th className="py-3 pr-4">Campanha</th>
                    <th className="py-3 pr-4 text-right">Resgates</th>
                    <th className="py-3 pr-4 text-right">Taxa uso</th>
                    <th className="py-3 pr-4 text-right">Clientes únicos</th>
                    <th className="py-3 pr-4 text-right">Descontos</th>
                    <th className="py-3 pr-4 text-right">Receita</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.length === 0 && <tr><td colSpan="7" className="py-8 text-center text-slate-500">Sem dados</td></tr>}
                  {report.rows.map(r => (
                    <tr key={r.id} className="border-b border-white/5" data-testid={`coupon-report-${r.code}`}>
                      <td className="py-2 pr-4 font-mono text-slate-100">{r.code}</td>
                      <td className="py-2 pr-4">
                        {r.campaign === "birthday-vip" ? <Pill tone="gold">🎂 Aniversário VIP</Pill> : (r.campaign ? <Pill tone="slate">{r.campaign}</Pill> : <span className="text-slate-500">—</span>)}
                      </td>
                      <td className="py-2 pr-4 text-right text-slate-100">{num(r.redemptions)}</td>
                      <td className="py-2 pr-4 text-right text-slate-300">{r.usage_rate_pct != null ? `${r.usage_rate_pct}%` : "—"}</td>
                      <td className="py-2 pr-4 text-right text-slate-300">{num(r.unique_customers)}</td>
                      <td className="py-2 pr-4 text-right text-rose-300">{brl(r.discount_cents)}</td>
                      <td className="py-2 pr-4 text-right text-emerald-400 font-semibold">{brl(r.revenue_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {report.recent.length > 0 && (
            <Card className="mt-6" title="Resgates recentes" kicker="Últimos 20">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <th className="py-2 pr-4">Quando</th>
                    <th className="py-2 pr-4">Cliente</th>
                    <th className="py-2 pr-4">Cupom</th>
                    <th className="py-2 pr-4 text-right">Total</th>
                    <th className="py-2 pr-4 text-right">Desconto</th>
                  </tr>
                </thead>
                <tbody>
                  {report.recent.map((u,i)=>(
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-2 pr-4 text-slate-400 text-xs">{dt(u.created_at)}</td>
                      <td className="py-2 pr-4">
                        <div className="text-slate-100">{u.customer_name || u.customer_email || "—"}</div>
                        <div className="text-[10px] text-slate-500">{u.customer_email}</div>
                      </td>
                      <td className="py-2 pr-4 font-mono text-[#d4a24c]">{u.code}</td>
                      <td className="py-2 pr-4 text-right text-slate-100">{brl(u.amount_total_cents)}</td>
                      <td className="py-2 pr-4 text-right text-rose-300">{brl(u.amount_discount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
